import {
  ChatBackend,
  ProductEventName,
  type DwellFrom,
  type MastraEventName,
  type ProductEventV2,
} from "@indeed/signals-schema/com/indeed/signals/schema/careerscout/ProductEventV2";

import type { GenericEventScreen } from "./constants";

export type ProductEventNameTag = keyof typeof ProductEventName;
export type ChatBackendTag = keyof typeof ChatBackend;

export interface ProductMetricInput {
  eventName: string;
  elementName?: string;
  screenName?: GenericEventScreen;
  additionalTags?: string[];
  mastraEventName?: keyof typeof MastraEventName;
  dwellFrom?: keyof typeof DwellFrom;
  productEventName?: ProductEventNameTag;
  chatBackend?: ChatBackendTag;
  proctorGroupsString?: string;
}

export function resolveProductEventName({
  eventName,
  elementName,
  productEventName,
}: Pick<
  ProductMetricInput,
  "eventName" | "elementName" | "productEventName"
>): ProductEventNameTag | null {
  if (productEventName) {
    return productEventName;
  }

  switch (eventName) {
    case "aceScreenView":
      return ProductEventName.ACE_SCREEN_VIEW;
    case "coWorkerError":
      return ProductEventName.ACE_ERROR_SCREEN;
    case "mastraNewChatStart":
      return ProductEventName.NEW_CHAT;
    case "mastraMessageSent":
      return ProductEventName.MESSAGE_SENT;
    case "mastraMessageReceived":
      return ProductEventName.MESSAGE_RECEIVED;
    default:
      break;
  }

  if (elementName === "signInScreen") {
    return ProductEventName.ACE_SIGN_IN_SCREEN;
  }

  if (elementName === "personalizedHubScreen") {
    return ProductEventName.HUB_SCREEN_VIEW;
  }

  return null;
}

export function resolveChatBackend({
  additionalTags,
  mastraEventName,
  chatBackend,
}: Pick<
  ProductMetricInput,
  "additionalTags" | "mastraEventName" | "chatBackend"
>): ChatBackendTag | null {
  if (chatBackend) {
    return chatBackend;
  }

  const isMastraRelated =
    additionalTags?.includes("mastra") === true || mastraEventName !== undefined;
  if (!isMastraRelated) {
    return null;
  }

  return ChatBackend.AI_CORE;
}

export function productEventNameToAvro(
  productEventName: ProductEventNameTag | null,
): ProductEventV2["productEventName"] {
  if (!productEventName) {
    return null;
  }

  return {
    "com.indeed.signals.schema.careerscout.ProductEventName": productEventName,
  };
}

export function chatBackendToAvro(
  chatBackend: ChatBackendTag | null,
): ProductEventV2["chatBackend"] {
  if (!chatBackend) {
    return null;
  }

  return {
    "com.indeed.signals.schema.careerscout.ChatBackend": chatBackend,
  };
}
